/**
 * Communication languages, shown with their initials (owner, 2026-09-21: "EN, HI, TE ...").
 * Codes are ISO 639-1, which is what `preferred_language` and `language_code` store.
 */
export const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'hi', name: 'Hindi' },
  { code: 'bn', name: 'Bengali' },
  { code: 'te', name: 'Telugu' },
  { code: 'ta', name: 'Tamil' },
  { code: 'kn', name: 'Kannada' },
  { code: 'ml', name: 'Malayalam' },
  { code: 'mr', name: 'Marathi' },
  { code: 'gu', name: 'Gujarati' },
  { code: 'pa', name: 'Punjabi' },
  { code: 'or', name: 'Odia' },
  { code: 'as', name: 'Assamese' },
  { code: 'ne', name: 'Nepali' },
] as const;

/** "EN - English". The initials first, because that is what a field officer scans for. */
export function languageOption(code: string): { id: string; label: string } {
  const entry = LANGUAGES.find((language) => language.code === code);
  return { id: code, label: `${code.toUpperCase()} - ${entry?.name ?? code}` };
}

export function languageName(code?: string | null): string {
  if (!code) return '--';
  const entry = LANGUAGES.find((language) => language.code === code);
  return entry ? `${code.toUpperCase()} - ${entry.name}` : code.toUpperCase();
}

/**
 * The one language of each state beyond English and Hindi (owner, 2026-09-21: "Based on the
 * state, filter the languages. English and Hindi are commonly available."; 2026-09-27: "we can
 * have 3 languages only. english, hindi, and state language"). Keyed by the state's name as the
 * geography master spells it. A Hindi-speaking state has no third language, so it offers two.
 */
const STATE_LANGUAGE: Record<string, string | null> = {
  'West Bengal': 'bn',
  'Uttar Pradesh': null,
  Rajasthan: null,
  'Andhra Pradesh': 'te',
  Telangana: 'te',
  Karnataka: 'kn',
  Kerala: 'ml',
  'Tamil Nadu': 'ta',
  Maharashtra: 'mr',
  Gujarat: 'gu',
  Punjab: 'pa',
  Odisha: 'or',
  Assam: 'as',
  Bihar: null,
  Jharkhand: null,
  Chhattisgarh: null,
  'Madhya Pradesh': null,
};

/** English, Hindi, then the state's own language. No state known yet: English and Hindi. */
export function languagesForState(stateName?: string | null): Array<{ id: string; label: string }> {
  const own = stateName ? STATE_LANGUAGE[stateName] : null;
  return [...new Set(['en', 'hi', ...(own ? [own] : [])])].map(languageOption);
}

/**
 * The language a farmer's consent is taken in beside English (owner, 2026-09-28: "default english +
 * second selected language (mandatory based on the state) -- select as initial value"): the state's
 * own language, or Hindi in a Hindi-speaking state. Null until the state is known.
 */
export function stateLanguage(stateName?: string | null): string | null {
  if (!stateName || !(stateName in STATE_LANGUAGE)) return null;
  return STATE_LANGUAGE[stateName] ?? 'hi';
}

/** English first, then the state's language, then whatever else was chosen, in its order. */
export function withRequiredLanguages(order: string[], stateName?: string | null): string[] {
  const second = stateLanguage(stateName);
  return [...new Set(['en', ...(second ? [second] : []), ...order])];
}
